/**
 * PrepareChatImageUploadUseCase
 *
 * Client-side preparation of a chat IMAGE message for confidential upload.
 * Orchestrates, in order:
 *   1. Resize + JPEG-compress the source image into a full image + a thumbnail
 *      (`ChatImageProcessingService` — the work the server ffmpeg passes used
 *      to do; now client-side because chat media is E2EE).
 *   2. Encrypt EACH rendition with the chat ECDH→AES-GCM binary path
 *      (`EncryptBinaryUseCase`, Task 9 — random per-file IV prepended). The
 *      full image and thumbnail are encrypted under the SAME peer key (the one
 *      `InitializeTextEncryptionUseCase.execute` derived for this conversation).
 *   3. Pair each ciphertext with its deterministic upload path so the send flow
 *      uploads to exactly where Task 5's reworked `transcodeChatImage` expects
 *      to find it (`{fileDir}image/{fileName}` and
 *      `{fileDir}thumbnail/{fileName}` — `MediaUtils.getChatImageCiphertextUploadPaths`).
 *
 * Web-first design — no Flutter parity (chat media encryption is Web-only).
 *
 * PURE DOMAIN: this use case does NOT upload, write Firestore, or dispatch. It
 * returns the two encrypted ciphertexts + their target paths; the
 * presentation/data send flow (Task 13) performs the actual storage uploads to
 * those paths and then fires the existing `transcodeChatContent` callable. The
 * CF derives the same paths server-side, so no path is carried in the payload.
 *
 * Pre-condition: the chat E2EE key was derived for the intended peer via
 * `InitializeTextEncryptionUseCase.execute`. The single-key-slot serialization
 * caveat from `EncryptBinaryUseCase`'s JSDoc applies — the caller serializes
 * init→prepare per peer.
 *
 * Null-key contract: when no key is derived, `EncryptBinaryUseCase` is a
 * pass-through (returns the plaintext bytes unchanged). This use case does not
 * special-case that; it would upload plaintext, which is acceptable only in the
 * never-initialized degenerate case (the send flow always initializes first).
 */

import {
  ChatImageProcessingService,
  chatImageProcessingService,
} from '@/domain/services/chatMedia/chatImageProcessingService';
import {
  EncryptBinaryUseCase,
  encryptBinaryUseCase,
} from '@/domain/usecases/textEncryption/encryptBinaryUseCase';
import { MediaUtils } from '@/domain/utils/mediaUtils';

/** The two encrypted renditions + their deterministic upload destinations. */
export interface PreparedChatImageUpload {
  /** Encrypted full-image ciphertext (`[ iv ‖ ciphertext ]`). */
  encryptedImage: ArrayBuffer;
  /** Encrypted thumbnail ciphertext. */
  encryptedThumbnail: ArrayBuffer;
  /**
   * RELATIVE bucket path the encrypted full image MUST be uploaded to —
   * `{fileDir}image/{fileName}`. The CF writes this verbatim to `serverPath`.
   */
  imageUploadPath: string;
  /**
   * RELATIVE bucket path the encrypted thumbnail MUST be uploaded to —
   * `{fileDir}thumbnail/{fileName}`. The CF writes this verbatim to `thumbnail`.
   */
  thumbnailUploadPath: string;
}

export class PrepareChatImageUploadUseCase {
  private readonly imageProcessingService: ChatImageProcessingService;
  private readonly encryptBinaryUseCase: EncryptBinaryUseCase;

  constructor(
    imageProcessingService: ChatImageProcessingService = chatImageProcessingService,
    binaryEncryptUseCase: EncryptBinaryUseCase = encryptBinaryUseCase,
  ) {
    this.imageProcessingService = imageProcessingService;
    this.encryptBinaryUseCase = binaryEncryptUseCase;
  }

  /**
   * Produce the encrypted full image + thumbnail and their upload paths.
   *
   * @param source - The user-selected chat image File/Blob.
   * @param baseUploadPath - The 4-segment `chat/{user1}/{user2}/{fileName}`
   *   path (the same value the send flow passes as the transcode payload's
   *   `filePath`). Used to derive the deterministic ciphertext paths.
   * @returns The two encrypted blobs + their deterministic upload paths.
   * @throws if resize/compress or encryption fails — the caller aborts the
   *   send so nothing partial is uploaded.
   */
  async execute(source: Blob, baseUploadPath: string): Promise<PreparedChatImageUpload> {
    // (1) Resize + compress: produce BOTH renditions client-side.
    const { fullImage, thumbnail } = await this.imageProcessingService.process(source);

    // (2) Encrypt EACH rendition with the chat E2EE binary path (Task 9).
    const fullImageBytes = await fullImage.arrayBuffer();
    const thumbnailBytes = await thumbnail.arrayBuffer();
    const encryptedImage = await this.encryptBinaryUseCase.execute(fullImageBytes);
    const encryptedThumbnail = await this.encryptBinaryUseCase.execute(thumbnailBytes);

    // (3) Pair each ciphertext with the deterministic path the CF (Task 5)
    // derives — the send flow uploads to exactly these paths.
    const { imagePath, thumbnailPath } =
      MediaUtils.getChatImageCiphertextUploadPaths(baseUploadPath);

    return {
      encryptedImage,
      encryptedThumbnail,
      imageUploadPath: imagePath,
      thumbnailUploadPath: thumbnailPath,
    };
  }
}

/** Shared singleton — mirrors the other use-case singletons. */
export const prepareChatImageUploadUseCase = new PrepareChatImageUploadUseCase();
